import moment from 'moment/moment';
import React, { useMemo } from 'react';
import {
  Button,
  CellTypes,
  DataGrid,
  DataGridProps,
  GridColDef,
  Badge,
  BadgeProps,
} from '@fashionnovateam/nova-ui-toolkit';
import { Link } from 'react-router-dom';
import { startCase } from 'lodash';
import { getPageLink } from '../UserRow';
import { getApplicationStatus } from '../../../Lib/Utils/FormUtils/vendor-application';
import ApplicationStatus from '../../../Lib/Constants/vendor-application-status';
import { User, Vendor } from '../../../Lib/Api/vendor';
import Roles, {
  hasAccessTo,
  Pages,
  RolesType,
  UserRoleDisplay,
} from '../../../Lib/Constants/roles';
import VendorTypeLabels from '../../../Lib/Constants/vendor-types';

type VendorRow = {
  id: number;
  vendor_name: CellTypes.DataGridCellCompanyParams;
  application_status: CellTypes.DataGridCellBadgeParams;
  country: CellTypes.DataGridCellStringParams;
  type: CellTypes.DataGridCellBadgeParams;
  netsuite_id: CellTypes.DataGridCellStringParams;
  email: CellTypes.DataGridCellStringParams;
  role: CellTypes.DataGridCellCustomParams;
  domestic_merch_po_eligible: CellTypes.DataGridCellCustomParams;
  last_logged_in_at: CellTypes.DataGridCellDateTimeParams;
  login_as: CellTypes.DataGridCellCustomParams;
  netsuite_status: CellTypes.DataGridCellBadgeParams | null;
  merch_type: CellTypes.DataGridCellBadgeParams;
};

type Props = {
  rows: User[];
  currentUser: User;
} & Omit<DataGridProps<VendorRow>, 'columns'>;

const getRoleProperties = (
  role: RolesType,
): { color: BadgeProps['color']; children: string; contrast: 'low' } => {
  let color: BadgeProps['color'] = 'neutral';
  let children: string;
  const contrast = 'low' as const;

  switch (role) {
    case Roles.VENDOR:
      color = 'primary';
      children = 'Vendor Admin';
      break;
    case Roles.VENDOR_OPERATIONS:
      color = 'yellow';
      children = 'Vendor Operations';
      break;
    case Roles.VENDOR_STAFF:
      color = 'purple';
      children = 'Vendor Staff';
      break;
    default:
      color = 'neutral';
      children = UserRoleDisplay[role] || role;
  }

  return { color, children, contrast };
};

function VendorsDataGrid({ rows, currentUser, ...dataGridProps }: Props) {
  const vendorColumns: GridColDef[] = [
    {
      field: 'vendor_name',
      headerName: 'Vendor Name',
      render: 'company',
      flex: 1,
      sortable: true,
      minWidth: 150,
      disableColumnMenu: true,
    },
    {
      flex: 1,
      field: 'application_status',
      headerName: 'Application Status',
      render: 'badge',
      sortable: false,
      minWidth: 180,
      disableColumnMenu: true,
    },
    {
      flex: 0,
      field: 'country',
      headerName: 'Country',
      sortable: true,
      display: 'flex',
      minWidth: 75,
      disableColumnMenu: true,
    },
    {
      field: 'merch_type',
      headerName: 'Vendor Type',
      render: 'badge',
      flex: 1,
      sortable: false,
      minWidth: 150,
      disableColumnMenu: true,
    },
    {
      field: 'netsuite_status',
      headerName: 'Netsuite Status',
      render: 'badge',
      flex: 1,
      sortable: false,
      minWidth: 120,
      disableColumnMenu: true,
    },
    {
      flex: 1,
      field: 'netsuite_id',
      headerName: 'Netsuite ID',
      render: 'string',
      display: 'flex',
      sortable: false,
      minWidth: 85,
      disableColumnMenu: true,
    },
    {
      flex: 1,
      field: 'email',
      headerName: 'User Email',
      render: 'email',
      sortable: true,
      minWidth: 150,
      disableColumnMenu: true,
    },
    {
      flex: 1,
      field: 'role',
      headerName: 'Role',
      render: 'custom',
      sortable: false,
      minWidth: 150,
      disableColumnMenu: true,
    },
    {
      flex: 1,
      field: 'domestic_merch_po_eligible',
      headerName: 'Domestic Eligible',
      render: 'custom',
      sortable: false,
      minWidth: 50,
      disableColumnMenu: true,
    },
    {
      flex: 1,
      field: 'last_logged_in_at',
      headerName: 'Last Login',
      render: 'dateTime',
      sortable: true,
      minWidth: 150,
      disableColumnMenu: true,
    },
    {
      field: 'login_as',
      headerName: '',
      render: 'custom',
      display: 'flex',
      align: 'center',
      sortable: false,
      minWidth: 100,
      disableColumnMenu: true,
    },
  ];

  const vendorName = (vendor: Vendor) => {
    if (vendor.is_individual) {
      if (!vendor.first_name && !vendor.last_name) return '-';
      return `${vendor.first_name} ${vendor.last_name}`;
    }
    return vendor.company_name || vendor.legal_name || '-';
  };

  const applicationStatusMapping: {
    [key: string]: 'yellow' | 'green' | 'red' | undefined;
  } = {
    REJECTED: 'red',
    APPROVED: 'green',
    PENDING_APPROVAL: 'yellow',
    PENDING_ACCOUNTING_APPROVAL: 'yellow',
    APPLICATION_PENDING: 'yellow',
  };

  const transformedRows = useMemo(
    () =>
      rows.map((u) => {
        const { vendor } = u;
        if (!vendor) {
          return undefined;
        }
        const status = getApplicationStatus(vendor || {});
        const page = getPageLink(
          currentUser.role,
          u.role,
          ApplicationStatus.APPROVED === status,
        );
        const isActive = vendor?.inactive === false;
        const hasNetsuiteId = !!vendor?.internal_id;

        const { color, children, contrast } = getRoleProperties(
          u.role as RolesType,
        );
        const isExisting = String(vendor.existing_vendor) === 'true';
        const existingVendor = isExisting ? 'Existing' : 'New';
        let vendorTypeLabel = existingVendor;
        if (
          vendor.merch_type &&
          VendorTypeLabels[vendor.merch_type as keyof typeof VendorTypeLabels]
        ) {
          vendorTypeLabel = `${existingVendor} - ${VendorTypeLabels[vendor.merch_type as keyof typeof VendorTypeLabels]}`;
        }

        return {
          id: u.id,
          vendor_name: { name: vendorName(vendor) },
          application_status: {
            contrast: 'low',
            children: startCase(status.toLowerCase()),
            color: applicationStatusMapping[status] ?? 'neutral',
          },
          country: vendor.country,
          type: {
            contrast: 'low',
            children: vendor.is_individual ? 'Individual' : 'Company',
            color: 'neutral',
          },
          merch_type: {
            children: vendorTypeLabel,
            color: existingVendor === 'New' ? 'green' : 'neutral',
            contrast: 'low',
          },
          netsuite_status: hasNetsuiteId
            ? {
                children: isActive ? 'Active' : 'Inactive',
                contrast: 'low',
                color: isActive ? 'green' : 'neutral',
              }
            : null,
          netsuite_id: vendor.internal_id || '-',
          email: u.email,
          role: (
            <Link to={`/admin-console/permissions?roles=${u.role}`}>
              <Badge color={color} contrast={contrast}>
                {children}
              </Badge>
            </Link>
          ),
          domestic_merch_po_eligible: (
            <Badge
              color={u.vendor?.domestic_merch_po_eligible ? 'green' : 'red'}
              contrast="low"
            >
              {u.vendor?.domestic_merch_po_eligible ? 'Yes' : 'No'}
            </Badge>
          ),
          last_logged_in_at: {
            date: moment(u.last_logged_in_at).toISOString(),
            activity: true,
            time: true,
          },
          login_as: hasAccessTo(
            `users/:id/${page}` as Pages,
            currentUser.role as RolesType,
          ) ? (
            <Link to={`/users/${u.id}/${page}`}>
              <Button size="sm" color="secondary">
                Login As
              </Button>
            </Link>
          ) : null,
        } satisfies VendorRow;
      }),
    [rows],
  );

  return (
    <DataGrid
      autosizeOnMount
      hideFilterButton
      hideGroupButton
      rows={transformedRows}
      paginationMode="server"
      sortingMode="server"
      initialState={{
        pinnedColumns: {
          left: ['vendor_name'],
        },
      }}
      {...dataGridProps}
      columns={vendorColumns}
    />
  );
}

export default VendorsDataGrid;
